import { cn } from "@/utils/cn";
import { Mention } from "@tiptap/extension-mention";
import type { SuggestionOptions } from "@tiptap/suggestion";
import { ReactRenderer } from "@tiptap/react";
import { fetchQuery, graphql } from "react-relay";
import type {
	mentionsExtensionQuery,
	mentionsExtensionQuery$data,
} from "./__generated__/mentionsExtensionQuery.graphql";
import { environment } from "@/relay/environment";
import tippy, { type Instance, type Props } from "tippy.js";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type UserListRef = {
	onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

const UserList = forwardRef<
	UserListRef,
	{
		items: mentionsExtensionQuery$data["searchUser"]["edges"][number]["node"][];
		command: (props: { id: string }) => void;
	}
>((props, ref) => {
	const [selectedIndex, setSelectedIndex] = useState(0);

	const selectItem = (index: number) => {
		const item = props.items[index];

		if (item) {
			props.command({ id: item.username! });
		}
	};

	const upHandler = () => {
		setSelectedIndex(
			(selectedIndex + props.items.length - 1) % props.items.length,
		);
	};

	const downHandler = () => {
		setSelectedIndex((selectedIndex + 1) % props.items.length);
	};

	const enterHandler = () => {
		selectItem(selectedIndex);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
	useEffect(() => setSelectedIndex(0), [props.items, setSelectedIndex]);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }) => {
			if (event.key === "ArrowUp") {
				upHandler();
				return true;
			}

			if (event.key === "ArrowDown") {
				downHandler();
				return true;
			}

			if (event.key === "Enter") {
				enterHandler();
				return true;
			}

			return false;
		},
	}));

	if (!props.items.length) {
		return null;
	}

	return (
		<div
			className={cn(
				// Base styles
				"isolate w-fit rounded-md p-1",
				// Invisible border that is only visible in `forced-colors` mode for accessibility purposes
				"outline outline-transparent focus:outline-hidden",
				// Handle scrolling when menu won't fit in viewport
				"overflow-y-auto",
				// Popover background
				"bg-popover text-popover-foreground border",
				// Shadows
				"shadow-md",
				// Transitions
				"transition data-closed:data-leave:opacity-0 data-leave:duration-100 data-leave:ease-in",
			)}
		>
			{props.items.map((it, index) => (
				<Button
					key={it.id}
					type="button"
					variant="ghost"
					className={cn(
						"w-full px-3 flex items-center justify-start gap-2 py-1 mb-1 last:mb-0",
						index === selectedIndex && "bg-accent",
					)}
					onClick={() => selectItem(index)}
				>
					<Avatar>
						<AvatarFallback>{it.avatarFallback || "AB"}</AvatarFallback>
						<AvatarImage src={it.photo?.signedUrl} />
					</Avatar>
					<div className="flex flex-col items-start">
						<span className="text-sm font-medium leading-tight">
							{it.displayName}
						</span>
						{it.displayName !== `@${it.username}` && (
							<span className="text-xs text-muted-foreground leading-tight">
								@{it.username}
							</span>
						)}
					</div>
				</Button>
			))}
		</div>
	);
});

function renderSuggestions({
	club,
}: {
	club: string;
}): Omit<SuggestionOptions<any>, "editor"> {
	let component: ReactRenderer<UserListRef> | undefined;
	let popup: Instance<Props>[] | undefined;

	return {
		async items({ query }) {
			const data = await fetchQuery<mentionsExtensionQuery>(
				environment,
				graphql`
          query mentionsExtensionQuery($query: String!, $club: String) {
            searchUser(query: $query, club: $club) {
              edges {
                node {
                  id
                  username
                  displayName
                  avatarFallback
                  photo {
                    signedUrl
                  }
                }
              }
            }
          }
        `,
				{ query, club },
			).toPromise();

			return data?.searchUser.edges.map((edge) => edge.node) || [];
		},

		render() {
			return {
				onStart: (props) => {
					component = new ReactRenderer(UserList, {
						props,
						editor: props.editor,
					});

					if (!props.clientRect) {
						return;
					}

					popup = tippy("body", {
						getReferenceClientRect: props.clientRect as () => DOMRect,
						appendTo: () => document.body!,
						content: component.element,
						showOnCreate: true,
						interactive: true,
						trigger: "manual",
						placement: "bottom-start",
					});
				},

				onUpdate: (props) => {
					component?.updateProps(props);

					if (!props.clientRect) {
						return;
					}

					popup?.[0].setProps({
						getReferenceClientRect: props.clientRect as () => DOMRect,
					});
				},

				onKeyDown(props) {
					if (props.event.key === "Escape") {
						popup?.[0].hide();
						return true;
					}

					if (!component?.ref) {
						return false;
					}

					return component.ref?.onKeyDown(props);
				},

				onExit() {
					popup?.[0].destroy();
					component?.destroy();

					popup = undefined;
					component = undefined;
				},
			};
		},
	};
}

export const MentionsExtension = (club: string) =>
	Mention.configure({
		HTMLAttributes: {
			class:
				"rounded-md bg-primary p-1 font-sans text-sm font-medium leading-tight text-primary-foreground focus:outline-none",
		},
		suggestion: renderSuggestions({ club }),
	});
